import React, { Fragment, useEffect } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  ListItem,
  ListItemSecondaryAction,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import {
  isValidEmail,
  isValidExtensionNumber,
  isValidPhoneNumber,
  textToSnakeCase,
} from '../../utils/utils';
import { styles } from './styles';
import {
  IRestaurantEmailEdit,
  IRestaurantPhoneEdit,
  OPERATION_TYPES,
} from './interfaces';
import { getEmailAndPhoneTypes } from '../../redux/actions/restaurantsActions';
import { RestaurantEditListBox } from './RestaurantEditListBox';

interface IProps {
  phones: IRestaurantPhoneEdit[];
  emails: IRestaurantEmailEdit[];
  onChange(
    updatedPhones: (IRestaurantPhoneEdit | IRestaurantEmailEdit)[] | null,
    type: string,
    name?: string,
    valid?: boolean,
    errorMessage?: string,
  ): void;
  errors: object;
  handleError(name: string, isValid: boolean, message?: string): void;
}

const defaultEmptyPhone: IRestaurantPhoneEdit = {
  operation: OPERATION_TYPES.ADD,
  id: null,
  number: '',
  extension: '',
  type: 15,
};

const defaultEmptyEmail: IRestaurantEmailEdit = {
  operation: OPERATION_TYPES.ADD,
  id: null,
  value: '',
  type: 3,
};

export const RestaurantEditContactInfo: React.FC<IProps> = ({
  phones,
  emails,
  onChange,
  errors,
  handleError,
}) => {
  let emailCollection: any[];
  emailCollection = [...emails];
  const classes = styles();
  const dispatch: any = useDispatch();
  const phoneTypes = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.phoneTypes,
  );
  let emailTypes = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.emailTypes,
  );
  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const { t } = useTranslation();

  useEffect(() => {
    handleGetPhoneAndEmailTypes();
  }, []);

  useEffect(() => {
    const restaurantPhone = phones.find(
      phone => phone?.type === 15 && phone.operation !== 'delete',
    );
    handleError(
      'VALIDATION_MIN_PHONE_ERROR',
      typeof restaurantPhone !== 'undefined',
      t('restaurant.info.edit.minOnePhoneNum'),
    );

    const primaryEmail = emails.find(
      email => email?.type === 3 && email.operation !== 'delete',
    );
    handleError(
      'VALIDATION_MIN_EMAIL_ERROR',
      typeof primaryEmail !== 'undefined',
      t('restaurant.info.edit.minOneEmail'),
    );
  }, [phones, emails]);

  const handleGetPhoneAndEmailTypes = async () => {
    if (emailTypes.length === 0 || phoneTypes.length === 0) {
      await dispatch(getEmailAndPhoneTypes(brand));
    }
    phones?.forEach(validatePhoneRow);
    emails?.forEach(validateEmailRow);
  };

  const validatePhoneRow = (phone, rowIndex) => {
    if (phone.operation === OPERATION_TYPES.DELETE) {
      return true;
    }

    if (!phone.type) {
      handleError(
        `phone.type-${rowIndex}`,
        false,
        t('app.common.invalidPhoneType'),
      );
      return false;
    }

    if (!isValidExtensionNumber(phone.extension)) {
      handleError(
        `phone.extension-${rowIndex}`,
        false,
        t('app.common.invalidPhoneExtension'),
      );
      return false;
    }

    if (!isValidPhoneNumber(phone.number)) {
      handleError(
        `phone.number-${rowIndex}`,
        false,
        t('app.common.invalidPhoneNumber'),
      );
      return false;
    }
  };

  const validateEmailRow = (email, rowIndex) => {
    let valid = true;

    if (email.operation === OPERATION_TYPES.DELETE) {
      return valid;
    }
    if (!email.type) {
      handleError(
        `email.type-${rowIndex}`,
        false,
        t('app.common.invalidMailType'),
      );
      return false;
    }
    valid = isValidEmail(email.value);
    handleError(`email.value-${rowIndex}`, valid, t('app.common.invalidEmail'));
    return valid;
  };

  const handlePhoneChange = event => {
    const { name, value } = event.target;
    const nameArr = name.split('-');
    const index: number = +nameArr[1];
    let valid = true;
    const newPhone: IRestaurantPhoneEdit = { ...phones[index] };
    let errorMessageKey = '';

    let duplicatedIndex = phones.findIndex(
      phone =>
        phone.id !== newPhone.id &&
        phone.type === newPhone.type &&
        phone.number === newPhone.number &&
        phone.operation !== OPERATION_TYPES.DELETE,
    );
    if (duplicatedIndex >= 0) {
      handleError(`phone.number-${duplicatedIndex}`, true);
      handleError(`phone.extension-${duplicatedIndex}`, true);
    }

    newPhone[nameArr[0].replace('phone.', '')] = value;
    if (name.indexOf('extension') >= 0) {
      valid = isValidExtensionNumber(value);
      if (!valid) {
        return event.preventDefault();
      }
    } else if (name.indexOf('number') >= 0) {
      valid = isValidPhoneNumber(newPhone.number);
      errorMessageKey = 'invalidPhoneNumber';
    }

    if (name.indexOf('type') >= 0) {
      valid = newPhone.type ? true : false;
      errorMessageKey = 'invalidPhoneType';
    }

    const updatedPhones = [
      ...phones.slice(0, index),
      newPhone,
      ...phones.slice(index + 1),
    ];

    onChange(
      updatedPhones,
      'phones',
      name,
      valid,
      errorMessageKey !== '' ? t(`app.common.${errorMessageKey}`) : '',
    );
  };

  const handleEmailChange = event => {
    const { name, value } = event.target;
    const nameArr = name.split('-');
    const index = +nameArr[1];
    const updatedEmail: IRestaurantEmailEdit = { ...emails[index] };
    let valid = true;
    let errorMessageKey = '';

    let duplicatedIndex = emails.findIndex(
      email =>
        email.id !== updatedEmail.id &&
        email.type === updatedEmail.type &&
        email.value === updatedEmail.value &&
        email.operation !== OPERATION_TYPES.DELETE,
    );
    if (duplicatedIndex >= 0) {
      handleError(`email.value-${duplicatedIndex}`, true);
    }

    updatedEmail[nameArr[0].replace('email.', '')] = value;

    if (name.indexOf('value') >= 0) {
      valid = isValidEmail(value);
      errorMessageKey = 'invalidEmail';
    }

    if (valid) {
      valid = !emails.find(
        email =>
          email.type === updatedEmail.type &&
          email.value === updatedEmail.value &&
          email.operation !== OPERATION_TYPES.DELETE,
      );
      if (!valid) {
        errorMessageKey = 'invalidDuplicateEmail';
      }
    }

    if (name.indexOf('type') >= 0) {
      valid = updatedEmail.type ? true : false;
      errorMessageKey = 'invalidMailType';
    }

    const updatedEmails = [
      ...emails.slice(0, index),
      updatedEmail,
      ...emails.slice(index + 1),
    ];
    emailCollection = [...updatedEmails];
    onChange(
      updatedEmails,
      'emails',
      name,
      valid,
      errorMessageKey !== '' ? t(`app.common.${errorMessageKey}`) : '',
    );
  };

  const handleAddMore = (dataType: string) => () => {
    let updatedData: (IRestaurantPhoneEdit | IRestaurantEmailEdit)[] | [] = [];

    if (dataType === 'phones') {
      updatedData = [...phones, defaultEmptyPhone];
      validatePhoneRow(defaultEmptyPhone, updatedData.length - 1);
    } else if (dataType === 'emails') {
      updatedData = [...emails, defaultEmptyEmail];
      emailCollection = [...updatedData];
      validateEmailRow(defaultEmptyEmail, updatedData.length - 1);
    }

    onChange(updatedData, dataType);
  };

  const handleDelete = (dataType: string, index: number) => () => {
    if (dataType === 'phones') {
      let updatedData: IRestaurantPhoneEdit[] = [...phones];

      updatedData = [
        ...updatedData.slice(0, index),
        { ...updatedData[index], operation: OPERATION_TYPES.DELETE },
        ...updatedData.slice(index + 1),
      ];
      let duplicatedIndex = updatedData.findIndex(
        phone =>
          phone.type === updatedData[index].type &&
          phone.number === updatedData[index].number &&
          phone.operation !== OPERATION_TYPES.DELETE,
      );
      if (duplicatedIndex >= 0) {
        handleError(`phone.type-${duplicatedIndex}`, true);
        handleError(`phone.number-${duplicatedIndex}`, true);
        handleError(`phone.extension-${duplicatedIndex}`, true);
      }
      handleError(`phone.type-${index}`, true);
      handleError(`phone.number-${index}`, true);
      handleError(`phone.extension-${index}`, true);
      onChange(updatedData, dataType);
    } else if (dataType === 'emails') {
      let updatedData: IRestaurantEmailEdit[] = [...emails];

      updatedData = [
        ...updatedData.slice(0, index),
        { ...updatedData[index], operation: OPERATION_TYPES.DELETE },
        ...updatedData.slice(index + 1),
      ];
      let duplicatedIndex = updatedData.findIndex(
        email =>
          email.type === updatedData[index].type &&
          email.value === updatedData[index].value &&
          email.operation !== OPERATION_TYPES.DELETE,
      );
      if (duplicatedIndex >= 0) {
        handleError(`email.type-${duplicatedIndex}`, true);
        handleError(`email.value-${duplicatedIndex}`, true);
      }
      handleError(`email.type-${index}`, true);
      handleError(`email.value-${index}`, true);
      onChange(updatedData, dataType);
    }
  };

  const findFirstPrimaryEmail = (emailType, index) => {
    const selectFristPrimaryMail = emailCollection.find((primaryType, i) => {
      if (primaryType.type === 3 && primaryType.operation !== 'delete') {
        primaryType.index = i;
        return primaryType;
      }
    });
    if (
      emailType.value === selectFristPrimaryMail?.value &&
      emailType.type === 3 &&
      selectFristPrimaryMail.index === index
    ) {
      return false;
    } else {
      return true;
    }
  };

  const checkPrimaryMailIsOne = () => {
    let returnValue = false;
    if (emailCollection.length === 1) {
      returnValue = emailCollection[0].type === 3 ? false : true;
    } else {
      returnValue = true;
    }
    return returnValue;
  };

  return (
    <Fragment>
      <Typography variant="subtitle1">
        {t('restaurant.info.contactInformation')}
      </Typography>

      <RestaurantEditListBox
        title={t('restaurant.info.phones')}
        data={phones}
        filterKey={{ key: 'operation', value: 'delete' }}
        extraHeaderComponent={() =>
          errors['VALIDATION_MIN_PHONE_ERROR'] && (
            <Grid container>
              <Grid item xs={12}>
                <Typography className={classes.warning}>
                  {errors['VALIDATION_MIN_PHONE_ERROR']}
                </Typography>
              </Grid>
            </Grid>
          )
        }
        listItem={(phone, index) => (
          <Fragment
            key={
              phone.RestaurantPhoneNumberId
                ? phone.RestaurantPhoneNumberId
                : `defaultPhone-${index}`
            }
          >
            <ListItem className={classes.listItem}>
              {phoneTypes.length && (
                <FormControl
                  style={{ flex: 1, marginRight: '10px', marginTop: '3px' }}
                  error={`phone.type-${index}` in errors}
                >
                  <InputLabel
                    id={`phone.type${index}`}
                    style={{
                      backgroundColor: '#ffffff',
                      top: '-7px',
                      left: '10px',
                      zIndex: 1,
                    }}
                  >
                    {t('restaurant.info.edit.phoneType')}
                  </InputLabel>
                  <Select
                    margin="dense"
                    labelId={`phone.type${index}`}
                    id={`phone.type${index}`}
                    name={`phone.type-${index}`}
                    className={classes.inputClean}
                    variant="outlined"
                    value={phone.type}
                    onChange={handlePhoneChange}
                  >
                    {phoneTypes.map(phone => (
                      <MenuItem key={phone.id} value={phone.id}>
                        {t(
                          `app.common.phone_types.${textToSnakeCase(
                            phone.name,
                          )}`,
                          phone.name,
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors[`phone.type-${index}`]}
                  </FormHelperText>
                </FormControl>
              )}
              <TextField
                margin="dense"
                id={`phone.number${index}`}
                name={`phone.number-${index}`}
                label={t('restaurant.info.edit.phoneNumber')}
                className={classes.inputClean}
                style={{ flex: 1, marginRight: '10px' }}
                variant="outlined"
                value={phone.number}
                onChange={handlePhoneChange}
                error={`phone.number-${index}` in errors}
                helperText={errors[`phone.number-${index}`]}
              />
              <TextField
                margin="dense"
                id={`phone.extension-${index}`}
                name={`phone.extension-${index}`}
                label={t('restaurant.info.edit.phoneExtension')}
                className={classes.inputClean}
                style={{ flex: 0.7 }}
                variant="outlined"
                value={phone.extension}
                onChange={handlePhoneChange}
                error={`phone.extension-${index}` in errors}
                helperText={errors[`phone.extension-${index}`]}
              />

              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={handleDelete('phones', index)}
                >
                  <Delete />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>

            {index < phones.length - 1 && <Divider />}
          </Fragment>
        )}
        onAddMore={handleAddMore('phones')}
      />

      <RestaurantEditListBox
        title={t('restaurant.info.emails')}
        data={emails}
        filterKey={{ key: 'operation', value: 'delete' }}
        extraHeaderComponent={() =>
          errors['VALIDATION_MIN_EMAIL_ERROR'] && (
            <Grid container>
              <Grid item xs={12}>
                <Typography className={classes.warning}>
                  {errors['VALIDATION_MIN_EMAIL_ERROR']}
                </Typography>
              </Grid>
            </Grid>
          )
        }
        listItem={(email, index) => (
          <Fragment
            key={
              email.RestaurantEmailId
                ? email.RestaurantEmailId
                : `defaultEmail-${index}`
            }
          >
            <ListItem className={classes.listItem}>
              {emailTypes.length && checkPrimaryMailIsOne() && (
                <FormControl
                  style={{ flex: 0.8, marginRight: '10px', marginTop: '3px' }}
                  error={`email.type-${index}` in errors}
                >
                  <InputLabel
                    id={`email.type-${index}`}
                    style={{
                      backgroundColor: '#ffffff',
                      top: '-7px',
                      left: '10px',
                      zIndex: 1,
                    }}
                  >
                    {t('restaurant.info.edit.emailType')}
                  </InputLabel>
                  <Select
                    margin="dense"
                    labelId={`email.type-${index}`}
                    id={`email.type-${index}`}
                    name={`email.type-${index}`}
                    className={classes.inputClean}
                    variant="outlined"
                    value={email.type}
                    onChange={handleEmailChange}
                  >
                    {emailTypes.map(email => (
                      <MenuItem key={email.id} value={email.id}>
                        {t(
                          `app.common.email_type.${textToSnakeCase(
                            email.name,
                          )}`,
                          email.name,
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors[`email.type-${index}`]}
                  </FormHelperText>
                </FormControl>
              )}
              <TextField
                margin="dense"
                id={`email-${index}`}
                label={t('restaurant.info.edit.email', 'Email')}
                name={`email.value-${index}`}
                className={classes.inputClean}
                style={{ flex: 1 }}
                variant="outlined"
                value={email.value}
                onChange={handleEmailChange}
                error={`email.value-${index}` in errors}
                helperText={errors[`email.value-${index}`]}
              />

              <ListItemSecondaryAction>
                {findFirstPrimaryEmail(email, index) && (
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={handleDelete('emails', index)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>

            {index < emails.length - 1 && <Divider />}
          </Fragment>
        )}
        onAddMore={handleAddMore('emails')}
      />
    </Fragment>
  );
};
